import React from 'react';
import { BarChart, Trash2, Layers, Lightbulb } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов освещения
 */
const LampComparisonSidebar = ({ 
  savedLighting, 
  deleteSavedLighting, 
  loadSavedLighting 
}) => {
  // Если нет сохраненных расчетов
  if (!savedLighting || savedLighting.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-amber-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов освещения для сравнения.
        </p>
        <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-600 dark:text-amber-400">
              После выполнения расчета нажмите кнопку "Сохранить расчет", чтобы добавить его в список сравнения.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-amber-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedLighting.length} {savedLighting.length === 1 ? 'вариант' : savedLighting.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedLighting.map((lighting) => (
          <div 
            key={lighting.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {lighting.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedLighting(lighting.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedLighting(lighting.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип освещения:</span>
                <span className="font-medium">
                  {lighting.formData.lampType === 'led' ? 'Светодиодное' : 
                  lighting.formData.lampType === 'fluorescent' ? 'Люминесцентное' :
                  lighting.formData.lampType === 'halogen' ? 'Галогенное' :
                  lighting.formData.lampType === 'incandescent' ? 'Лампы накаливания' :
                  'Пользовательское'}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Мощность:</span>
                <span>
                  {formatNumber(lighting.results.totalPower, 0)} Вт
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Освещенность:</span>
                <span>
                  {formatNumber(lighting.results.actualIlluminance, 0)} лк
                </span>
              </div>
              <div className="flex justify-between">
                <span>Кол-во светильников:</span>
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  {lighting.results.recommendedLampCount} шт.
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Сравнительная таблица - будет показана, если есть не менее 2-х сохраненных расчетов */}
      {savedLighting.length >= 2 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Сравнение вариантов
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Параметр</th>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <th key={lighting.id} className="px-3 py-2 text-center text-gray-500 dark:text-gray-400 font-medium">
                      {lighting.formData.lampType === 'led' ? 'Светодиодное' : 
                      lighting.formData.lampType === 'fluorescent' ? 'Люминесцентное' :
                      lighting.formData.lampType === 'halogen' ? 'Галогенное' :
                      lighting.formData.lampType === 'incandescent' ? 'Лампы накаливания' :
                      'Польз.'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">Освещенность</td>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <td key={lighting.id} className="px-3 py-2 text-center text-gray-900 dark:text-white font-medium">
                      {formatNumber(lighting.results.actualIlluminance, 0)} лк
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">Светильники</td>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <td key={lighting.id} className="px-3 py-2 text-center text-gray-900 dark:text-white font-medium">
                      {lighting.results.recommendedLampCount} шт.
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">Мощность</td>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <td key={lighting.id} className="px-3 py-2 text-center text-gray-900 dark:text-white font-medium">
                      {formatNumber(lighting.results.totalPower, 0)} Вт
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">Энергоэффективность</td>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <td key={lighting.id} className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium
                        ${lighting.results.energyEfficiency === 'excellent' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 
                        lighting.results.energyEfficiency === 'good' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 
                        lighting.results.energyEfficiency === 'average' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' : 
                        lighting.results.energyEfficiency === 'poor' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' : 
                        'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'}`}
                      >
                        {lighting.results.energyEfficiency === 'excellent' ? 'Отлично' : 
                        lighting.results.energyEfficiency === 'good' ? 'Хорошо' : 
                        lighting.results.energyEfficiency === 'average' ? 'Средне' : 
                        lighting.results.energyEfficiency === 'poor' ? 'Низкая' : 
                        'Очень низкая'}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">Соответствие нормам</td>
                  {savedLighting.slice(0, 3).map((lighting) => (
                    <td key={lighting.id} className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium
                        ${lighting.results.standardCompliance === 'optimal' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 
                        lighting.results.standardCompliance === 'minimal' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' : 
                        lighting.results.standardCompliance === 'below' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' : 
                        lighting.results.standardCompliance === 'excessive' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' : 
                        'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'}`}
                      >
                        {lighting.results.standardCompliance === 'optimal' ? 'Оптимально' : 
                        lighting.results.standardCompliance === 'minimal' ? 'Минимально' : 
                        lighting.results.standardCompliance === 'below' ? 'Ниже нормы' : 
                        lighting.results.standardCompliance === 'excessive' ? 'Избыточно' : 
                        'Нет данных'}
                      </span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LampComparisonSidebar;